import React from 'react';
import getName from '../../../../../../utils/getName';
import { getIntl } from '../../../../../../utils/intl';
import { useLoadedUser } from '../../../../../../hooks/api/useUser';
import { useNestedNavigation, useProfilesTabNavigation } from '../../../../../../utils/appNavigationNames';
import useNotes from '../../../../../../hooks/api/useNotes';
import { PromptActions, StandardPrompt } from '../../../../../v2BaseComponents/Prompt';
import MainContentContainer from '../../../../../v2BaseComponents/MainContentContainer';
import { ContentStack } from '../../../../../v2BaseComponents/Stack';
import Text from '../../../../../v2BaseComponents/Text';
import { AnimateLoadedContent } from '../../../../../v2BaseComponents/Loader';

const intl = getIntl('mainApp.profiles.registrationAddProfileModal', {
    promptHeader: 'Who is this Medical Profile for?',
    promptSubtext: 'Create a Medical Profile for yourself or someone else at no cost to explore Primary Record.',
    confirmationPretext: 'For Me - ',
    forSomeoneElse: 'For Someone Else',
    skip: 'Skip',
});

type RegistrationAddProfileModalProps = {
    isOpen: boolean;
    onClose: () => void;
};

const RegistrationAddProfileModal: React.FC<RegistrationAddProfileModalProps> = ({ isOpen, onClose }) => {
    const { user } = useLoadedUser({});
    const navigator = useProfilesTabNavigation();
    const { nestedNotesNavigate } = useNestedNavigation();
    const { notes, isLoading: isLoadingNotes } = useNotes({
        noteQueryArgs: {
            entityType: 'user',
        },
    });
    const defaultNote = notes?.notes?.[0];

    const handleSkip = React.useCallback(() => {
        if (defaultNote)
            nestedNotesNavigate('details', {
                noteId: defaultNote.noteId,
            });
        onClose();
    }, [defaultNote, nestedNotesNavigate, onClose]);

    return (
        <StandardPrompt title={intl('promptHeader')} isOpen={isOpen} setIsOpen={(isOpen) => !isOpen && handleSkip()}>
            <AnimateLoadedContent isLoading={isLoadingNotes}>
                {() => (
                    <MainContentContainer>
                        <ContentStack>
                            <Text variant="body-1">{intl('promptSubtext')}</Text>
                            <PromptActions
                                actions={[
                                    {
                                        title: `${intl('confirmationPretext')}${getName(
                                            {
                                                firstName: user.preferredName || user.firstName,
                                                lastName: user.lastName,
                                            },
                                            {
                                                abbreviateLastName: true,
                                            },
                                        )}`,
                                        onPress: () => {
                                            navigator.navigate('add', { useUser: 'true' });
                                            onClose();
                                        },
                                        testID: 'registration-add-profile-modal-for-me-button',
                                    },
                                    {
                                        title: intl('forSomeoneElse'),
                                        onPress: () => {
                                            navigator.navigate('add', { useUser: 'false' });
                                            onClose();
                                        },
                                        testID: 'registration-add-profile-modal-for-someone-else-button',
                                    },
                                    {
                                        title: intl('skip'),
                                        onPress: handleSkip,
                                        variant: 'secondary',
                                        testID: 'registration-add-profile-modal-skip-button',
                                    },
                                ]}
                            />
                        </ContentStack>
                    </MainContentContainer>
                )}
            </AnimateLoadedContent>
        </StandardPrompt>
    );
};

export default React.memo(RegistrationAddProfileModal);
